import { z } from "zod";
import { alarmKinds, alarmStatuses, connectionStates, dataStates, exportStatuses, menuCodes, qualityCodes, } from "./enums.js";
export const idSchema = z.string().trim().min(1).max(64);
export const isoDateTimeSchema = z.iso.datetime({ offset: true });
export const loginSchema = z.object({
    username: z.string().trim().min(3).max(64),
    password: z.string().min(8).max(128),
});
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(20),
});
export const createOperatorSchema = z.object({
    username: z.string().trim().min(3).max(64),
    displayName: z.string().trim().min(1).max(100),
    password: z.string().min(8).max(128),
    groupIds: z.array(idSchema).default([]),
    menuKeys: z.array(z.enum(menuCodes)).default([]),
});
export const updateOperatorSchema = createOperatorSchema
    .omit({ password: true })
    .partial()
    .extend({ enabled: z.boolean().optional() });
export const createDeviceSchema = z.object({
    internalCode: z.string().trim().min(1).max(64),
    name: z.string().trim().min(1).max(120),
    productKey: z.string().trim().min(1).max(64),
    deviceName: z.string().trim().min(1).max(128),
    serialNumber: z.string().trim().max(128).optional().nullable(),
    location: z.string().trim().max(255).optional().nullable(),
    groupId: idSchema.optional().nullable(),
    wiringMode: z.enum(["three_phase_three_wire", "three_phase_four_wire", "unknown"]).default("unknown"),
    transformerRatio: z.number().positive().default(1),
    enabled: z.boolean().default(true),
    notes: z.string().max(1000).optional().nullable(),
});
export const updateDeviceSchema = z.object({
    internalCode: z.string().trim().min(1).max(64).optional(),
    name: z.string().trim().min(1).max(120).optional(),
    productKey: z.string().trim().min(1).max(64).optional(),
    deviceName: z.string().trim().min(1).max(128).optional(),
    serialNumber: z.string().trim().max(128).optional().nullable(),
    location: z.string().trim().max(255).optional().nullable(),
    groupId: idSchema.optional().nullable(),
    wiringMode: z.enum(["three_phase_three_wire", "three_phase_four_wire", "unknown"]).optional(),
    transformerRatio: z.number().positive().optional(),
    enabled: z.boolean().optional(),
    notes: z.string().max(1000).optional().nullable(),
    effectiveAt: isoDateTimeSchema.optional(),
});
export const batchDeviceImportSchema = z.object({
    items: z.array(createDeviceSchema).min(1).max(1000),
    dryRun: z.boolean().default(true),
});
const nullableNumber = z.number().finite().nullable().optional();
export const normalizedTelemetrySchema = z.object({
    sourceTime: isoDateTimeSchema,
    voltageA: nullableNumber,
    voltageB: nullableNumber,
    voltageC: nullableNumber,
    voltageAb: nullableNumber,
    voltageBc: nullableNumber,
    voltageCa: nullableNumber,
    currentA: nullableNumber,
    currentB: nullableNumber,
    currentC: nullableNumber,
    activePowerA: nullableNumber,
    activePowerB: nullableNumber,
    activePowerC: nullableNumber,
    activePowerTotal: nullableNumber,
    reactivePowerTotal: nullableNumber,
    apparentPowerTotal: nullableNumber,
    powerFactorA: nullableNumber,
    powerFactorB: nullableNumber,
    powerFactorC: nullableNumber,
    powerFactorTotal: nullableNumber,
    frequency: nullableNumber,
    importActiveEnergyTotal: nullableNumber,
    exportActiveEnergyTotal: nullableNumber,
    qualityCode: z.enum(qualityCodes).default("good"),
});
export const simulationMessageSchema = z.object({
    messageId: z.string().trim().min(1).max(128),
    productKey: z.string().trim().min(1).max(64),
    deviceName: z.string().trim().min(1).max(128),
    topic: z.string().trim().min(1).max(512),
    messageType: z.enum(["property", "event", "status"]),
    sourceTime: isoDateTimeSchema,
    payload: z.record(z.string(), z.unknown()),
});
export const telemetryQuerySchema = paginationSchema.extend({
    from: isoDateTimeSchema,
    to: isoDateTimeSchema,
    granularity: z.enum(["raw", "minute", "hour", "day"]).default("raw"),
});
export const createAlarmRuleSchema = z.object({
    name: z.string().trim().min(1).max(120),
    kind: z.enum(alarmKinds),
    field: z.string().trim().min(1).max(64),
    operator: z.enum(["gt", "gte", "lt", "lte", "eq"]),
    threshold: z.number().finite(),
    durationSeconds: z.number().int().min(0).max(86400).default(0),
    recoveryThreshold: z.number().finite().optional().nullable(),
    groupId: idSchema.optional().nullable(),
    enabled: z.boolean().default(true),
});
export const alarmActionSchema = z.object({
    action: z.enum(["process", "close"]),
    note: z.string().trim().min(1).max(1000),
});
export const listAlarmsSchema = paginationSchema.extend({
    status: z.enum(alarmStatuses).optional(),
    deviceId: idSchema.optional(),
});
export const createExportSchema = z.object({
    type: z.enum(["devices", "telemetry", "alarms", "audit"]),
    filters: z.record(z.string(), z.unknown()).default({}),
    timezone: z.string().min(1).max(64).default("Asia/Shanghai"),
});
export const exportStatusSchema = z.enum(exportStatuses);
export const connectionStateSchema = z.enum(connectionStates);
export const dataStateSchema = z.enum(dataStates);
//# sourceMappingURL=schemas.js.map